import { shallowEqual } from 'fast-equals';
import { useLazyGetCaptainUpTokenQuery } from 'features/api/serverSideApiSlice';
import { setCaptainUpError } from 'features/dashboard/dashboardSlice';
import { useInternalSession } from 'hooks';
import { RootState, useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { SessionUser } from 'next-auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DeepPartial } from 'types/swagger';
import { isServerSide } from 'utility/constant';
import { int2roman } from 'utility/dashboard/functions';
import { dynamicDataLoader, isMatch } from 'utility/functions';

interface stCaptainUpType {
  level: string;
  points: number;
  monete: number;
  nextLevel: number;
  isReady: boolean;
  isError: boolean;
}

const captainUpEmpty: stCaptainUpType = {
  level: '',
  points: 0,
  monete: 0,
  nextLevel: 0,
  isReady: false,
  isError: false,
};

interface CaptainUpHookType extends stCaptainUpType {
  // end: () => Promise<void>;
  start: () => Promise<boolean>;
}

interface cptSessionUser {
  id: string;
  name: string;
}
interface cptUser {
  token: string;
  user: cptSessionUser;
}

const selectGenericFactory =
  <T>(path: string[]) =>
  (state: RootState) =>
    dynamicDataLoader<RootState, T>(state, path, true);

const mergeCaptainUpToken =
  () =>
  (authUser, query): DeepPartial<cptUser> => {
    const { name, userIdIms: id } = authUser ?? {};

    if (!name) {
      return { user: undefined, token: undefined } as DeepPartial<cptUser>;
    }
    const user = { user: { id, name } } as cptUser;

    if (query) {
      const queryName = Object.keys(query ?? {}).find((x) =>
        isMatch(x, `^getCaptainUpToken\\((\\S*)("|')?name("|')?:("|')?${name}("|')?`)
      );
      const result = Reflect.get(query ?? {}, queryName ?? 'nope');

      if (result?.status === 'fulfilled') {
        Reflect.set(user ?? {}, 'token', result?.data?.token);
      }
    }
    return user;
  };

const useSelectCaptainUpToken = () => {
  const traceRef = useRef(false);

  useEffect(() => {
    if (isServerSide) return;

    const search = new URLSearchParams(document.location.search.substring(1));
    const trace = search.get('trace') ?? '';
    traceRef.current = trace.toLowerCase().includes('plus');
  }, []);

  const getUser = useMemo(() => selectGenericFactory<SessionUser>(['auth', 'user']), []);
  const getQuery = useMemo(() => selectGenericFactory(['api_server', 'queries']), []);
  const getCaptainUpToken = useMemo(() => mergeCaptainUpToken(), []);

  const auth = useTypedSelector(getUser, shallowEqual);
  if (traceRef.current) {
    console.log(`SnaiPlus: user`, auth);
  }
  const query = useTypedSelector(getQuery, shallowEqual);
  if (traceRef.current) {
    console.log(`SnaiPlus: query`, query);
  }
  const result = getCaptainUpToken(auth, query);
  if (traceRef.current) {
    console.log(`SnaiPlus: result`, result);
  }
  return result;
};

export const useCaptainUp = (): CaptainUpHookType => {
  const traceRef = useRef(false);
  const loadingRef = useRef(false);
  const { isAuthenticated } = useInternalSession();

  const dispatch = useAppDispatch();
  const [getCaptainUpToken] = useLazyGetCaptainUpTokenQuery();

  const [state, setState] = useState<stCaptainUpType>(captainUpEmpty);
  const { level, points, monete, isReady, isError, nextLevel } = state;
  const stToken = useSelectCaptainUpToken();

  const getCaptainUpUser = useCallback(async (): Promise<cptUser | undefined> => {
    let { user, token } = stToken ?? {};

    if (token) {
      if (traceRef.current) {
        console.log(`SnaiPlus: token from state`);
      }
      return stToken as cptUser;
    }

    if (!user) {
      if (traceRef.current) {
        console.log(`SnaiPlus: user not foud`);
        return undefined;
      }
    }

    const cptToken = await getCaptainUpToken(user as cptSessionUser).unwrap();
    return { ...cptToken, user } as cptUser;
  }, [stToken, getCaptainUpToken]);

  const doInit = async ({ token, user }: cptUser): Promise<boolean> => {
    let isOK = false;
    try {
      const { captain } = (window ?? {}) as any;
      const config = {
        user,
        lang: 'it-it', // Player language
        api_key: process.env.NEXT_PUBLIC_CAPTAIN_UP_APP_API_KEY, // Your app API key
        country: 'italy', // Player country
        currency: 'eur', // Player currency
        inboxSync: true, // Whether to sync inbox data
        signed_user: token, // Generated hash for user, see: https://captainup.com/help/javascript/user-integration#toc-secure-user-integration
        client_token: process.env.NEXT_PUBLIC_CAPTAIN_UP_CLIENT_TOKEN_KEY,
      };

      await captain.up(config);
      if (traceRef.current) {
        console.log(`SnaiPlus: captain.init`);
      }
      await captain.isReady();

      const prPlayer = captain.user.get();
      const prNextLevel = captain.levels.getNext();

      const [{ level, currencies }, nextLevel] = await Promise.all([prPlayer, prNextLevel]);

      setState((prev) => {
        if (traceRef.current) {
          console.log(`SnaiPlus: captain.user + captain.levels`);
        }
        return structuredClone({
          ...prev,
          level: int2roman(level.number),
          points: currencies.points,
          monete: currencies.monete,
          isReady: true,
          isError: false,
          nextLevel: nextLevel.points,
        });
      });
      isOK = true;
    } catch (error) {
      if (traceRef.current) {
        console.error(`SnaiPlus: captain.user or captain.levels error`);
      }
      setState({ ...captainUpEmpty, isError: true });
    }

    if (traceRef.current) {
      console.log(`SnaiPlus: doInit`, isOK);
    }

    return isOK;
  };

  const start = useCallback(async (): Promise<boolean> => {
    if (isServerSide) return false;

    let result = false;
    loadingRef.current = true;
    try {
      const user = await getCaptainUpUser();
      if (user) {
        result = await doInit(user);
      }
    } catch (e) {
      console.error(e);
    }
    loadingRef.current = false;
    if (traceRef.current) {
      console.log(`SnaiPlus: run`, result);
    }

    return result;
  }, [getCaptainUpUser]);

  // const end = async () : Promise<void> => {
  //   if (isSSR) return
  //
  //   const { captain } = (window ?? {}) as any;
  //   if (typeof captain?.user?.logout !== 'undefined') {
  //     if (traceRef.current) {
  //       console.log(`SnaiPlus: off`);
  //     }
  //     await captain.user.logout();
  //   }
  // }

  useEffect(() => {
    if (!isAuthenticated) return;
    if (loadingRef.current) return;
    if (isError) return;
    if (isReady) return;
    if (isServerSide) return;

    const { captain } = (window ?? {}) as any;

    if (typeof captain !== 'undefined') {
      if (traceRef.current) {
        console.log(`SnaiPlus: dry run`);
      }
      start();
    }
  }, [isAuthenticated, isError, isReady, start]);

  useEffect(() => {
    dispatch(setCaptainUpError(isError));
  }, [isError, dispatch]);

  useEffect(() => {
    const search = new URLSearchParams(document.location.search.substring(1));
    const trace = search.get('trace') ?? '';
    traceRef.current = trace.toLowerCase().includes('plus');
  }, []);

  return { level, points, monete, isReady, isError, nextLevel, start };
};
